/**
 * AIConsentModal - Disclosure and consent for AI-powered features
 * 
 * Required by Apple App Store Guidelines 5.1.1(i) and 5.1.2(i).
 * Shows before first use of Photo, Receipt, or Barcode modes that send
 * user data to third-party AI services.
 */

import React from 'react';
import { createPortal } from 'react-dom';
import '../styles/components/aiConsentModal.css';

const AI_CONSENT_KEY_PREFIX = 'ai-data-consent';

/** Build a per-user localStorage key. Falls back to global key when userId is unavailable. */
const consentKey = (userId?: number): string =>
  userId ? `${AI_CONSENT_KEY_PREFIX}-${userId}` : AI_CONSENT_KEY_PREFIX;

interface AIConsentModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onDecline: () => void;
  userId?: number;
}

/** Check if user has already given AI consent */
export const hasAIConsent = (userId?: number): boolean => {
  return localStorage.getItem(consentKey(userId)) === 'granted';
};

/** Record that user gave AI consent */
export const grantAIConsent = (userId?: number): void => {
  localStorage.setItem(consentKey(userId), 'granted');
  // Also set global key for backward compat with older code paths
  if (userId) localStorage.setItem(AI_CONSENT_KEY_PREFIX, 'granted');
};

/** Revoke AI consent */
export const revokeAIConsent = (userId?: number): void => {
  localStorage.removeItem(consentKey(userId));
  if (userId) localStorage.removeItem(AI_CONSENT_KEY_PREFIX);
};

const AIConsentModal: React.FC<AIConsentModalProps> = ({ isOpen, onAccept, onDecline, userId }) => {
  if (!isOpen) return null;

  const handleAccept = () => {
    grantAIConsent(userId);
    onAccept();
  };

  return createPortal(
    <div className="ai-consent-overlay" onClick={onDecline}>
      <div className="ai-consent-modal" onClick={e => e.stopPropagation()}>
        <div className="ai-consent-header">
          <h2>AI-Powered Features</h2>
        </div>

        <div className="ai-consent-body">
          <p className="ai-consent-intro">
            Photo, Receipt, and Barcode scanning use AI to identify items in your images. Before using these features, please review how your data is handled:
          </p>

          <div className="ai-consent-section">
            <h3>What data is sent</h3>
            <p>Your photos are sent to an AI service for analysis. Only the image is sent — no personal information such as your name, email, or account details is included.</p>
          </div>

          <div className="ai-consent-section">
            <h3>Who receives the data</h3>
            <p>Images are processed by <strong>Google Gemini</strong> (Google LLC). In rare cases, <strong>OpenAI</strong> (OpenAI, Inc.) may be used as a fallback service.</p>
          </div>

          <div className="ai-consent-section">
            <h3>How the data is used</h3>
            <p>Images are used solely to identify and describe items. They are not stored permanently by AI providers and are not used to train AI models.</p>
          </div>

          <div className="ai-consent-note">
            <strong>Prefer not to share photos?</strong> You can always add items using Manual mode, which does not send any data to AI services.
          </div>

          <p className="ai-consent-policy">
            For full details, see our{' '}
            <button
              type="button"
              className="ai-consent-policy-link"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.open('/help/privacy', '_blank', 'noopener,noreferrer');
              }}
            >
              Privacy Policy
            </button>.
          </p>
        </div>

        <div className="ai-consent-footer">
          <button className="ai-consent-decline-btn" onClick={onDecline}>
            Not Now
          </button>
          <button className="ai-consent-accept-btn" onClick={handleAccept}>
            I Agree
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AIConsentModal;
